import {useIsFocused, useNavigation} from '@react-navigation/native';
import React, {useEffect, useMemo, useRef, useState} from 'react';

import {
  BackHandler,
  Linking,
  Platform,
  RefreshControl,
  ScrollView,
} from 'react-native';
import {CouponFilterSheet} from './components/CouponFilterSheet';
import ReactNativeHapticFeedback, {
  HapticFeedbackTypes,
} from 'react-native-haptic-feedback';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import {CouponsShimmer} from '../../components/Interface/Loadings/CouponsShimmer';

import {CouponCarousel} from './components/CouponCarousel';

import {VoucherDTO} from '../../dtos/VoucherDTO';
import {api} from '../../services/api';
import {rastrear} from '../../services/rastrear';

import {
  Container,
  SectionChevBtn,
  SectionCountBadge,
  SectionCountBadgeText,
  SectionHeader,
  SectionHeaderWrapper,
  SectionIcon,
  SectionIconBg,
  SectionItem,
  SectionItemTitle,
  SectionItemWrapper,
  SectionTitleRow,
} from './styles';

//import {BuyCouponCard} from './components/BuyCouponCard';

import {ProfileHeader} from '../../components/Interface/Headers/ProfileHeader';
import {ScreenTitle} from '../../components/shared/ScreenTitle';
import {
  HeaderIconButton,
  HeaderIconButtonsRow,
} from '../../components/Interface/Headers/HeaderIconButton';

import {useAuth} from '../../context/auth';
import {useGlobal} from '../../context/global';
import {useCarrinhos} from '../../context/useCarrinhos';
import {showFinesToast} from '../../context/finesFlow';

import {addInAppTrigger} from '../../lib/pushMessaging';
import {getPersistentDeviceId} from '../../utils/deviceId';
import {useRemoteConfig} from '../../context/useRemoteConfig';
import {differenceInDays} from 'date-fns';
import {RFValue} from 'react-native-responsive-fontsize';
import {PromoCard} from '../../components/shared/PromoCard';
import {useTheme} from 'styled-components/native';
import {BuyCouponCard} from './components/BuyCouponCard';
import {CarrinhoProgressCard} from './components/CarrinhoProgressCard';

export function Coupons() {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const {isLogged, setShowSign, openSign, setSignDismissable, isAuthReady} =
    useAuth();
  const {telaNovaCupom, fetchRemoteConfigCupom} = useGlobal();
  const {refetch: refetchCarrinhos} = useCarrinhos();

  useEffect(() => {
    if (Platform.OS !== 'web') {
      addInAppTrigger('screen', 'coupons');
    }
  }, []);

  const hasCalledRastrear = useRef(false);

  useEffect(() => {
    if (!isFocused) {
      hasCalledRastrear.current = false;
      return;
    }
    if (hasCalledRastrear.current) {
      return;
    }
    hasCalledRastrear.current = true;
    rastrear({status_tela: 'cupons'});
  }, [isFocused]);

  const [vouchers, setVouchers] = useState<VoucherDTO[]>([] as VoucherDTO[]);
  const [expiredVouchers, setExpiredVouchers] = useState<VoucherDTO[]>(
    [] as VoucherDTO[],
  );
  const {supportHelpText} = useRemoteConfig(true);

  const supportPhone =
    supportHelpText.find(item =>
      ['phone', 'whatsapp', 'phone_number'].includes(item.type),
    )?.value ?? '';
  const supportPhoneDigits = supportPhone.replace(/\D/g, '');

  useEffect(() => {
    BackHandler.addEventListener('hardwareBackPress', function () {
      return false;
    });
  }, [isFocused]);

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  let isMounted = true;

  async function syncVouchers() {
    try {
      const response = await api.get('/vouchers/sync');

      return response;
    } catch (error: any) {
      console.log(error.response.data);
    }
  }

  async function fetchVouchers(isRefresh = false) {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setIsLoading(true);
    }

    syncVouchers();
    refetchCarrinhos();

    try {
      const deviceId = await getPersistentDeviceId();

      const response = await api.get(`/vouchers?device_id=${deviceId}`, {
        timeout: 15000,
      });

      const availableVouchers = response.data.filter(
        (item: VoucherDTO) => item.valido,
      );

      const expiredVouchers = response.data.filter(
        (item: VoucherDTO) => !item.valido,
      );

      if (isMounted) {
        if (availableVouchers) {
          setVouchers(availableVouchers.reverse());
        }
        if (expiredVouchers) {
          setExpiredVouchers(expiredVouchers.reverse());
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      if (isMounted) {
        if (isRefresh) {
          setRefreshing(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }

  useEffect(() => {
    if (isLogged) {
      fetchVouchers();
    } else {
      if (isMounted) {
        setIsLoading(false);
        setVouchers([]);
        setExpiredVouchers([]);
      }
    }
    fetchRemoteConfigCupom();
    return () => {
      isMounted = false;
    };
  }, [isFocused, isLogged]);

  // Dispara o toast de multas só quando a lista termina de carregar (transição
  // true -> false de isLoading, não apenas "isLoading é false"). O guard de
  // "já mostrado" fica em finesFlow (module-level), então persiste entre idas
  // e vindas à tela e só reseta quando o app é reaberto do zero.
  const prevIsLoadingRef = useRef(isLoading);

  useEffect(() => {
    if (!isFocused) {
      prevIsLoadingRef.current = isLoading;
      return;
    }

    const finishedLoading = prevIsLoadingRef.current && !isLoading;
    prevIsLoadingRef.current = isLoading;

    if (finishedLoading) {
      showFinesToast();
    }
  }, [isFocused, isLoading]);

  function refresh() {
    fetchVouchers();
  }

  function handleVoucherDetails(data: VoucherDTO) {
    const isMobileViewport =
      window.innerWidth <= 768 && window.innerHeight <= 1024;

    if (Platform.OS === 'web') {
      // Para web, se a tela nova de cupom estiver ativa e for um dispositivo móvel, abrir a tela nova de cupom
      return navigation.navigate(
        telaNovaCupom && isMobileViewport ? 'CouponNovo' : 'Coupon',
        {
          data,
        },
      );
    } else {
      return navigation.navigate(telaNovaCupom ? 'CouponNovo' : 'Coupon', {
        data,
      });
    }
  }

  const isInvalidVoucher = (item: VoucherDTO) =>
    String(item?.id ?? '').trim().length === 0;

  const getValidityLabel = (dtValidade: string) => {
    const dias = differenceInDays(new Date(dtValidade), new Date());
    if (dias <= 0) {
      return 'Expirado';
    }
    return `${dias} ${dias === 1 ? 'dia' : 'dias'}`;
  };

  const buildSupportMessage = (item: VoucherDTO) => {
    const VALOR_TOTAL_NO_CENTS = item.valorTotalJurosCents;
    const LITRAGEM =
      item.tipo === 'C' ? `${item.liters} Litros` : 'Nao se aplica';
    const REGIAO = item.region || item.localidade || '-';
    const VALOR_JUROS =
      VALOR_TOTAL_NO_CENTS != null
        ? Number(VALOR_TOTAL_NO_CENTS).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })
        : '-';

    return [
      'Ola, meu cupom está bloqueado. Preciso de ajuda!',
      '',
      '*Dados do cupom*',
      `Produto: ${item.name || '-'}`,
      `Litragem: ${LITRAGEM}`,
      `Categoria: ${item.categoria || '-'}`,
      `Regiao: ${REGIAO}`,
      `Dias de validade: ${getValidityLabel(item.dt_validade)}`,
      `Valor total com juros: ${VALOR_JUROS}`,
      `Metodo de pagamento: ${item.paymentMethodo || '-'}`,
    ].join('\n');
  };

  const handleRefundPress = (_item: VoucherDTO) => {
    navigation.navigate('Reembolsos' as never);
  };

  const handleSupportPress = (item: VoucherDTO) => {
    if (!supportPhoneDigits) {
      return;
    }

    const message = buildSupportMessage(item);
    const whatsappLink = `https://api.whatsapp.com/send/?phone=${supportPhoneDigits}&text=${encodeURIComponent(
      message,
    )}&type=phone_number&app_absent=0`;

    Linking.openURL(whatsappLink);
  };

  useEffect(() => {
    if (Platform.OS === 'web' && isAuthReady) {
      if (!isLogged) {
        setSignDismissable(false);
        openSign();
      } else {
        setShowSign(false);
      }
    }
  }, [isLogged, isAuthReady]);

  const [selectedNav, setSelectedNav] = useState('disponiveis');

  // ─── Nav header horizontal scroll (setas) ──────────────────────────────────
  const NAV_ITEM_WIDTH = RFValue(125);
  const NAV_ITEM_GAP = RFValue(12);
  const NAV_SCROLL_STEP = NAV_ITEM_WIDTH + NAV_ITEM_GAP;
  const sectionScrollRef = useRef<ScrollView>(null as any);
  const [sectionScrollX, setSectionScrollX] = useState(0);
  const [sectionContainerWidth, setSectionContainerWidth] = useState(0);
  const [sectionContentWidth, setSectionContentWidth] = useState(0);
  const canScrollNavLeft = sectionScrollX > 4;
  const canScrollNavRight =
    sectionContentWidth - sectionContainerWidth - sectionScrollX > 4;

  function scrollNav(direction: 'left' | 'right') {
    const nextX =
      direction === 'left'
        ? Math.max(0, sectionScrollX - NAV_SCROLL_STEP)
        : Math.min(
            sectionContentWidth - sectionContainerWidth,
            sectionScrollX + NAV_SCROLL_STEP,
          );
    sectionScrollRef.current?.scrollTo({x: nextX, animated: true});
  }

  // ─── Filter state ────────────────────────────────────────────────────────────
  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedNames, setSelectedNames] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const uniqueNames = useMemo(
    () =>
      [...new Set([...vouchers, ...expiredVouchers].map(v => v.name))].sort(),
    [vouchers, expiredVouchers],
  );

  const uniqueCategories = useMemo(
    () =>
      [
        ...new Set(
          [...vouchers, ...expiredVouchers]
            .map(v => v.categoria)
            .filter(Boolean),
        ),
      ].sort(),
    [vouchers, expiredVouchers],
  );

  const toggleName = (name: string) =>
    setSelectedNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name],
    );

  const toggleCategory = (cat: string) =>
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat],
    );

  const clearFilters = () => {
    setSelectedNames([]);
    setSelectedCategories([]);
  };

  // ─── Nav tabs ────────────────────────────────────────────────────────────────
  type NavTab = {
    key: string;
    label: string;
    icon: string;
    type: 'tab';
    data: VoucherDTO[];
    emptyText: string;
  };
  type NavLink = {
    key: string;
    label: string;
    icon: string;
    type: 'link';
    screen: string;
  };
  type NavItem = NavTab | NavLink;

  const navItems: NavItem[] = [
    {
      key: 'disponiveis',
      label: 'Disponíveis',
      icon: 'ticket-confirmation-outline',
      type: 'tab',
      data: vouchers,
      emptyText: 'Você não possui cupons disponíveis.',
    },
    {
      key: 'expirados',
      label: 'Expirados',
      icon: 'clock-remove-outline',
      type: 'tab',
      data: expiredVouchers,
      emptyText: 'Você não possui cupons expirados.',
    },
    {
      key: 'reembolso',
      label: 'Reembolso',
      icon: 'cash-refund',
      type: 'link',
      screen: 'Reembolsos',
    },
    {
      key: 'historico',
      label: 'Histórico de compras',
      icon: 'clock-outline',

      type: 'link',
      screen: 'PaymentsHistory',
    },
  ];

  useEffect(() => {
    const isSelectedVisible = navItems.some(
      item => item.type === 'tab' && item.key === selectedNav,
    );
    if (!isSelectedVisible) {
      const firstTab = navItems.find(item => item.type === 'tab') as
        | NavTab
        | undefined;
      if (firstTab) {
        setSelectedNav(firstTab.key);
      }
    }
  }, [vouchers, expiredVouchers]);

  const activeTab = navItems.find(
    i => i.type === 'tab' && i.key === selectedNav,
  ) as NavTab | undefined;

  const filteredItems = useMemo(() => {
    const base = activeTab?.data ?? [];
    return base.filter(item => {
      const nameMatch =
        selectedNames.length === 0 || selectedNames.includes(item.name);
      const catMatch =
        selectedCategories.length === 0 ||
        selectedCategories.includes(item.categoria);
      return nameMatch && catMatch;
    });
  }, [activeTab, selectedNames, selectedCategories]);

  const theme = useTheme();

  return (
    <>
      <ProfileHeader />

      <Container>
        <ScrollView
          style={{flex: 1}}
          contentContainerStyle={{paddingBottom: RFValue(90)}}
          bounces
          showsVerticalScrollIndicator={false}
          overScrollMode="always"
          scrollEnabled={!isLoading}
          refreshControl={
            <RefreshControl
              refreshing={false}
              onRefresh={() => {
                ReactNativeHapticFeedback.trigger(
                  HapticFeedbackTypes.impactMedium,
                  {enableVibrateFallback: false},
                );
                fetchVouchers(true);
              }}
              tintColor="#17B70F"
              colors={['#17B70F']}
            />
          }>
          <ScreenTitle
            title="Cupons"
            rightContent={
              <HeaderIconButtonsRow>
                <HeaderIconButton icon="refresh" onPress={refresh} />
              </HeaderIconButtonsRow>
            }
          />
          {isLoading || refreshing ? (
            <CouponsShimmer />
          ) : (
            <>
              {activeTab &&
                (filteredItems.length === 0 ? (
                  <BuyCouponCard
                    onPress={() =>
                      Platform.OS !== 'web'
                        ? navigation.navigate('Shopping' as never)
                        : navigation.navigate('Localization', {tipo: 'C'})
                    }
                  />
                ) : (
                  <CouponCarousel
                    items={filteredItems}
                    onBuyPress={() =>
                      Platform.OS !== 'web'
                        ? navigation.navigate('Shopping' as never)
                        : navigation.navigate('Localization', {tipo: 'C'})
                    }
                    onPressCard={handleVoucherDetails}
                    onSupportPress={handleSupportPress}
                    onRefundPress={handleRefundPress}
                    isBlockedFn={isInvalidVoucher}
                  />
                ))}

              <SectionHeaderWrapper>
                <SectionHeader
                  ref={sectionScrollRef as any}
                  onLayout={e =>
                    setSectionContainerWidth(e.nativeEvent.layout.width)
                  }
                  onContentSizeChange={w => setSectionContentWidth(w)}
                  onScroll={e =>
                    setSectionScrollX(e.nativeEvent.contentOffset.x)
                  }
                  scrollEventThrottle={16}>
                  {navItems.map(item => {
                    const isSelected =
                      item.type === 'tab' && selectedNav === item.key;
                    const isDisabled =
                      item.key === 'expirados' && expiredVouchers.length === 0;
                    const handlePress = () =>
                      item.type === 'tab'
                        ? setSelectedNav(item.key)
                        : navigation.navigate(item.screen as never);
                    return (
                      <SectionItemWrapper
                        key={item.key}
                        selected={isSelected}
                        style={{opacity: isDisabled ? 0.5 : 1}}>
                        <SectionItem
                          onPress={isDisabled ? undefined : handlePress}
                          disabled={isDisabled}
                          activeOpacity={0.8}>
                          <SectionIconBg selected={isSelected}>
                            <SectionIcon
                              name={item.icon}
                              selected={isSelected}
                            />
                          </SectionIconBg>
                          <SectionTitleRow>
                            <SectionItemTitle selected={isSelected}>
                              {item.label}
                            </SectionItemTitle>
                            {item.type === 'tab' && (
                              <SectionCountBadge selected={isSelected}>
                                <SectionCountBadgeText selected={isSelected}>
                                  {item.data.length}
                                </SectionCountBadgeText>
                              </SectionCountBadge>
                            )}
                          </SectionTitleRow>
                        </SectionItem>
                      </SectionItemWrapper>
                    );
                  })}
                </SectionHeader>

                {canScrollNavLeft && (
                  <SectionChevBtn
                    side="left"
                    activeOpacity={0.75}
                    onPress={() => scrollNav('left')}>
                    <MaterialCommunityIcons
                      name="chevron-left"
                      size={RFValue(20)}
                      color="#fff"
                    />
                  </SectionChevBtn>
                )}

                {canScrollNavRight && (
                  <SectionChevBtn
                    side="right"
                    activeOpacity={0.75}
                    onPress={() => scrollNav('right')}>
                    <MaterialCommunityIcons
                      name="chevron-right"
                      size={RFValue(20)}
                      color="#fff"
                    />
                  </SectionChevBtn>
                )}
              </SectionHeaderWrapper>

              {Platform.OS !== 'web' && (
                <PromoCard
                  icon="gift"
                  iconBg={theme.colors.red_light}
                  iconColor={theme.colors.red}
                  title="Indique e Ganhe"
                  description="Convide seus amigos e ganhe dinheiro! Aproveite!"
                  onPress={() => navigation.navigate('ReferAndWin')}
                />
              )}

              {Platform.OS !== 'web' && <CarrinhoProgressCard />}
            </>
          )}
        </ScrollView>
      </Container>

      <CouponFilterSheet
        isOpen={filterVisible}
        onClose={() => setFilterVisible(false)}
        uniqueNames={uniqueNames}
        uniqueCategories={uniqueCategories}
        selectedNames={selectedNames}
        selectedCategories={selectedCategories}
        onToggleName={toggleName}
        onToggleCategory={toggleCategory}
        onClear={clearFilters}
      />
    </>
  );
}
